/**
 * Tiny SVG sparkline.
 *   - data: a (number | null)[] aligned to N days oldest -> newest.
 *   - tone: visual treatment ("ok" | "fail" | "warn") chooses stroke color.
 *
 * Renders:
 *   - flat dimmed baseline if data is empty or all null
 *   - a single dot if exactly one numeric value is present
 *   - a polyline through the numeric values otherwise (gaps interpolated
 *     by the SVG `polyline` natural drawing — we filter nulls out before
 *     plotting so the line connects available days)
 */

type Tone = "ok" | "fail" | "warn";

type Props = {
  data: (number | null)[];
  tone?: Tone;
  width?: number;
  height?: number;
};

function strokeFor(tone: Tone): string {
  if (tone === "fail") return "var(--bad)";
  if (tone === "warn") return "var(--warn)";
  return "var(--ok)";
}

export function SparkLine({ data, tone = "ok", width = 88, height = 22 }: Props) {
  const numericPoints = data
    .map((v, i) => ({ i, v }))
    .filter((p): p is { i: number; v: number } => typeof p.v === "number" && Number.isFinite(p.v));

  if (numericPoints.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
        <line
          x1="0" y1={height / 2} x2={width} y2={height / 2}
          stroke="var(--dim)" strokeWidth="1" strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const n = data.length;
  const values = numericPoints.map((p) => p.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  // Inset top/bottom by 1px so the line never clips
  const yFor = (v: number) => height - 1 - ((v - min) / span) * (height - 2);
  const xFor = (i: number) => (n <= 1 ? width / 2 : (i / (n - 1)) * width);

  const stroke = strokeFor(tone);

  if (numericPoints.length === 1) {
    const p = numericPoints[0];
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
        <line
          x1="0" y1={height / 2} x2={width} y2={height / 2}
          stroke="var(--dim)" strokeWidth="1" strokeDasharray="2 3"
        />
        <circle cx={xFor(p.i)} cy={yFor(p.v)} r="2" fill={stroke} />
      </svg>
    );
  }

  const points = numericPoints.map((p) => `${xFor(p.i).toFixed(1)},${yFor(p.v).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
